/*
 * Charabanc cart recommendations V2 — profit-weighted, scent-aware, A/B.
 *
 * Self-contained and unit-testable: this module touches NO theme globals. Everything it
 * needs (cart fetch, product hydrate, DOM nodes, money formatter, beacon transport, the
 * native-AI fallback) is passed in via CartRecoV2.load(opts). That lets the exact same code
 * run in the live cart drawer AND in the local test harness with mocked I/O.
 *
 * Contract with the backend (https://dash.../api):
 *   POST /cart/recommendations {cartToken, country, items:[{title,quantity,properties}]}
 *     -> {recommendations:[{key,handle,variant_id,title,category,scent_no,scent_name,reason,slot,mode}],
 *         mode, serve, abVariant}
 *   POST /cart/reco-event {cartToken, abVariant, mode, market, currency, events:[{event,key,variantId,slot}]}
 *
 * ZERO-REGRESSION DESIGN: on control arm / ineligible cart / error / timeout / empty, it calls
 * opts.onFallback() and the drawer keeps its existing native-AI + Liquid behavior untouched.
 */
(function () {
  'use strict';

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = String(s == null ? '' : s);
    return d.innerHTML;
  }

  // Cart bucket → arm. MUST stay byte-identical to backend recoModel.ts abBucket.
  // NO SPLIT IS RUNNING — every cart gets the graduated winner, arm E (glass-bulb card, 2026-10-11; D graduated 2026-09-10).
  // C-vs-D ran 2026-08-01→08-29; judged on that pre-registered window D took the collection NET
  // increment to exactly the pre-registered bar (8), earned $2.24 vs $1.70 margin per exposed cart
  // (+32%), and tied the slot-1 oil guardrail dead even (7.28% both) — no dilution.
  // The FNV-1a 50/50 hash lives in git history; restore it here (and in recoModel.ts, byte-identical)
  // for the next split.
  function abBucket(cartToken) {
    // QA override: ?reco_arm=B|C|D forces the arm (also pinned into the /recommendations POST in load()).
    var forced = forcedArm();
    if (forced) return forced;
    // EXP_BULB(发车 2026-09-10):D=[油,油,合集,第2台] vs E=[油,油,合集,玻璃瓶],同 4 张卡。
    // 2026-10-11 老板「玻璃瓶全量」:E 定版,所有车都拿 E(与后端 recoModel.ts BULB_GRADUATED 一致)。
    // 下面的哈希保留不删,下次分流去掉这一行 return 即可(后端同步改)。
    return 'E';
    // Uniform FNV-1a over the ?-stripped token → clean 50/50.
    // MUST stay byte-identical to backend recoModel.ts abBucket.
    var t = String(cartToken || '').split('?')[0];
    var h = 0x811c9dc5;                      // FNV-1a 32-bit offset basis
    for (var i = 0; i < t.length; i++) {
      h ^= t.charCodeAt(i);
      h = Math.imul(h, 0x01000193);          // × FNV prime, uint32 via imul
    }
    return ((h >>> 0) & 1) === 0 ? 'D' : 'E';
  }
  // QA only: read ?reco_arm=B|C from the URL. Returns null for real traffic (no param).
  function forcedArm() {
    try { var m = (window.location.search || '').match(/[?&]reco_arm=(B|C|D|E)(?:&|$)/); return m ? m[1] : null; }
    catch (e) { return null; }
  }

  function postJSON(url, body, timeoutMs) {
    var ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var t = ctl ? setTimeout(function () { ctl.abort(); }, timeoutMs || 1000) : null;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
      signal: ctl ? ctl.signal : undefined
    }).then(function (r) { if (t) clearTimeout(t); return r.json(); });
  }

  /** Fire-and-forget event POST. navigator.sendBeacon can't carry application/json cross-origin
   *  (no preflight → browser blocks it); fetch keepalive survives unload AND does normal CORS. */
  function sendEvent(url, obj) {
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(obj),
        credentials: 'omit',
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* telemetry must never break the drawer */ }
  }

  function beaconBody(event, cartToken, data, opts, items) {
    return {
      cartToken: cartToken,
      abVariant: data.abVariant,
      mode: data.mode,
      market: opts.country || '',
      currency: opts.currency || '',
      events: items.map(function (it) {
        return { event: event, key: it.key || '', variantId: it.variant_id || '', slot: it.slot || 0 };
      })
    };
  }

  /**
   * Data-driven discount for a variant, from the LIVE Shopify automatic discounts the backend read
   * (window.__cartRecoLast.data.cartDiscounts — see backend discountService). Returns {pct, badgeType}
   * the drawer may DISPLAY, or null. Display price = checkout price is GUARANTEED because we only
   * surface a discount when: (a) its buy-condition is met for this cart (eligible), (b) the variant
   * is entitled (getVariantIds — so a non-30ml or a brand-new oil not yet added to the discount is
   * correctly NOT shown), and (c) the get-quantity isn't already consumed by items in the cart
   * (fixes "companion caps at 1 oil but the 2nd was shown discounted too"). No hardcoded 0.4.
   */

  /**
   * 2026-09-16:companion 的名额是「一台机器一瓶」,不是「一单一瓶」。
   *
   * 实测(无码,直接读 /cart.js 的 line_level_discount_allocations):
   *   2机+1油 → 1 瓶拿到 −21.60      2机+2油 → **2 瓶都拿到**
   *   1机+2油 → 只 1 瓶拿到          3机+3油 → **3 瓶全拿到**
   * 折扣配置印证:Companion Oil Price 的 usesPerOrderLimit = null(每单不限次数),
   * customerBuys.quantity = 1(买方集合任 1 件),customerGets.quantity = 1 × 60% off。
   * ⇒ 可打折瓶数 = min(买方件数, 合格油瓶数)。
   *
   * 🔴🔴 绝不能用「名字里有没有 Diffuser」来数机器 —— 中台自己先写了那一版,被复核拦下:
   * 店里有 9 个商品 handle/标题带 diffuser 却**不在**买方集合里,其中 5 个在售 ——
   *   diffuser-glass-bulb(Diffuser Aroma Glass Bulb,正是购物车 D-vs-E 实验 E 臂主推的卡片)
   *   supplements-of-diffuser-light-bulb / supplements-of-diffuser-glass-bottle
   *   supplements-of-retro-aroma-diffuser-lamp / mini-ghost-diy-diffuser
   * 实测「玻璃灯泡 + 1 瓶油」Shopify 一分折扣都不给。按字符串数就会显示 6 折、结账收全价 ——
   * 那是**假价**(feedback_no_fake_compare_at),比现在这个「少报」严重得多。
   * 所以买方件只认 **buyProductIds**(由 Liquid 按买方集合 id 627880296774 逐件核对后烘进页面),
   * 拿不到这份名单就整个退回旧的保守行为(见调用处),永远 fail-closed。
   *
   * 占槽的油:只认 getVariantIds(折扣授权的那 16 个 30ml 变体)里、且**真的挂上了**
   * companion / 免费油折扣的行;被折成 $0 的免费油也算已占用。
   * ⚠️ 把免费油计入已占用是**兜底**(免费油可能不带码字符串到达),**不代表剩下的名额还能兑现**——
   *    实测空名额不会自动轮给 companion。本函数的返回值**不足以**判断 companion 会不会兑现,
   *    必须配合下方 variantDiscount() 里的 freeOilActive 闸,见那里的注释。
   * 全程按 id 判定,不猜商品名。
   *
   * 返回:剩余名额;**−1 = 没有权威名单,调用方请退回旧逻辑**。
   */
  function companionSlotsFree(items, buyProductIds, getVariantIds) {
    if (!buyProductIds || !buyProductIds.length) return -1;
    var slots = 0, used = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var qty = it.quantity || 1;
      if (buyProductIds.indexOf(String(it.product_id)) >= 0) slots += qty;
      if (!getVariantIds || getVariantIds.indexOf(String(it.variant_id)) < 0) continue;
      var taken = (it.final_line_price === 0);   // 免费油被折成 $0 也占槽
      var allocs = it.line_level_discount_allocations || [];
      for (var a = 0; a < allocs.length; a++) {
        var al = allocs[a] || {};
        var t = String(((al.discount_application || {}).title) || '');
        if (al.amount > 0 && (t.indexOf('Companion') >= 0 || t.indexOf('Free Oil') >= 0 || /welcomeoil/i.test(t))) taken = true;
      }
      if (taken) used += qty;
    }
    return slots - used;
  }

  function variantDiscount(variantId, variantPrice) {
    try {
      var last = window.__cartRecoLast;
      if (!last || !last.data || !last.data.cartDiscounts) return null;
      var vid = String(variantId);
      var items = last.cartItems || [];
      var ds = last.data.cartDiscounts;
      // WELCOMEOIL(免费油码)已占用"每台机器一瓶折扣油"的槽 → companion 60% 对额外油
      // 根本不生效(实测第2瓶$33)。绝不展示假的 companion 折扣价。
      var freeOilActive = (last.cartDiscountCodes || []).some(function (c) { return /^welcomeoil$/i.test(String(c)); });
      // ── 2026-09-16:门槛闸从「过门槛就藏」改窄成「只在互斥窄带里藏」(老板批准)──────
      // 旧逻辑:加这瓶油后小计 ≥ discount_threshold → 不展示 6 折。它要防的事是真的,但范围写宽了。
      // 真实机制(美国无码、带机+30ml油、5–9月 n=432 按毛小计分档实测):
      //    <$149 → 96.6% 拿到 companion;$149–150 → 12/12;**$150.01–163.99 → 2/33(6%)**;≥$164 → 96.1%
      //    10%OFF 的最低消费按「扣掉商品级折扣之后」的小计判:companion 先扣 $15 会把小计压到 $149 以下,
      //    10%OFF 就失去资格;Shopify 在「只给 companion」与「只给 10%OFF」之间**取对顾客更划算的那个**。
      //    $150 以上 10%OFF(≥$15.00)≥ companion($15)→ companion 被丢,油按全价进 10%OFF。
      //    #635103(MINI $79 + 3×$25 = $154,三瓶油 discount 全 0,实收 138.6 = 154×0.9)就是这一档的标准样本 ——
      //    07-25 加闸的人观察是对的;错在把窄带写成了「过门槛全藏」,把 ≥$164 那 96% 拿得到的车也藏了。
      // 新规则(与 Shopify 的判法逐档一致;所有量都是本币分,thr 已按结账币种换算):
      //    full = sub + P;comp = floor(P×pct);after = full − comp
      //    after ≥ thr        → 两者叠加,显示 6 折
      //    full  < thr        → 10%OFF 本就不成立,只有 companion,显示
      //    否则(互斥带)     → 比大小:floor(full×10%) ≥ comp → 10%OFF 赢 → **藏**;comp 更大 → 显示
      // ⚠️ 10% 这个比例与 discount_threshold 一样,是对「10%OFF」那条自动折扣的写死依赖;它一改这里要跟。
      // ⚠️ freeOilActive 与 getQty 两道闸是真的,保留不动。
      var thr = last.discountThreshold || 0;
      var sub = last.itemsSubtotalPrice || 0;
      var ORDER_DISCOUNT_RATE = 0.10;   // 「10%OFF」自动折扣的比例
      function companionLosesToOrderDiscount(pct) {
        if (!(thr > 0) || !(variantPrice > 0)) return false;
        var P = variantPrice, full = sub + P, comp = Math.floor(P * pct), after = full - comp;
        if (after >= thr) return false;          // 叠加
        if (full < thr) return false;            // 只有 companion
        return Math.floor(full * ORDER_DISCOUNT_RATE) >= comp;   // 互斥带:10%OFF 更大则 companion 被丢
      }
      for (var i = 0; i < ds.length; i++) {
        var d = ds[i];
        if (!d || !d.eligible || !d.pct || !d.getVariantIds || d.getVariantIds.indexOf(vid) < 0) continue;
        // 🔴🔴 这道闸**绝对不能删**,即使下面有了「一机一瓶」的名额账,它也不是冗余的。
        //
        // WELCOMEOIL 本身也是 BXGY,买方集合与 Companion **同一个**(id 627880296774),买1件→得1件
        // 100% off,usesPerOrderLimit 同样为 null,授权变体 12 个(Companion 是 16 个)。
        // 两条 BXGY 抢同一个买方集合,而 **Shopify 的分配不可预测 —— 它不是剩余名额的函数**。
        // 2026-09-16 实测(NO.68/NO.71 在两个名单里,NO.15 只有 Companion 授权,全部带 WELCOMEOIL):
        //   2机 + NO.68 + NO.15            剩 1 名额 → NO.15 拿到 **6 折**(码被判 applicable:false)
        //   3机 + NO.68 + NO.71 + NO.15    剩 1 名额 → NO.15 **全价**,一台机器空转
        //   4机 + NO.68 + NO.71 + NO.15    剩 2 名额 → NO.15 **全价**,两台机器空转
        //   5机 + NO.68 + NO.71 + NO.15    剩 3 名额 → NO.15 拿到 **6 折**(两条折扣同车共存)
        // **同样剩 1 个名额,一个给 6 折一个给全价** ⇒ 任何 `slotsFree > 0 ⇒ 6 折会兑现` 的推理都不成立。
        // 也不成立的说法:「两者绝不混用」(3机/5机那两档就是混用)、「空名额会轮给 companion」(不会)。
        //
        // ⇒ 只要车里有免费油活着,companion 一律按**不兑现**处理(fail-closed)。
        //   companionSlotsFree() 在 4 机那档会算出「还剩 2 个名额」→ 显示 6 折 →
        //   顾客看 $10、结账收 $25 = **假价**(feedback_no_fake_compare_at)。挡住它的就是这一行。
        //
        // 🔴 这一行的存在理由是**不可预测性**,不是「绝不混用」。
        //    **复现出「混用」的车不构成删除它的理由** —— 混用本来就会发生,我们只是无法预测何时发生。
        // ⚠️ 只认 WELCOMEOIL 这一个码;将来再开别的免费油码,必须同时加进 freeOilActive 的判断。
        // 📏 已知并接受的少报:码被判 applicable:false(companion 实际在给 6 折)时这里仍然会藏,
        //    因为 line ~342 的 cartDiscountCodes 丢掉了 applicable 字段。**丢钱不骗人**,单独再修,
        //    修之前必须验「车里已有的油会不会因为状态翻转而悄悄涨价」这条迁移,别搭本补丁的车。
        if (freeOilActive && d.badgeType === 'companion') continue;
        // 2026-09-16:窄带判定取代旧的 overThreshold(见上方注释)。
        if (d.badgeType === 'companion' && companionLosesToOrderDiscount(d.pct)) continue;
        // 一机一瓶:名额 = 买方件数 − 已占槽的油瓶数。旧代码在这里用 getQty(=1)封顶**全车**,
        // 把「第二台机器的第二瓶」错判成全价。slotsFree < 0 表示拿不到买方名单 → 退回旧逻辑。
        var slotsFree = (d.badgeType === 'companion')
          ? companionSlotsFree(items, last.companionBuyProductIds, d.getVariantIds)
          : -1;
        if (slotsFree >= 0) {
          if (slotsFree <= 0) continue;
        } else if (d.getQty > 0) {
          var used = 0;
          for (var k = 0; k < items.length; k++) {
            if (d.getVariantIds.indexOf(String(items[k].variant_id)) >= 0) used += (items[k].quantity || 1);
          }
          if (used >= d.getQty) continue;    // get-quantity exhausted → this variant won't actually discount
        }
        return { pct: d.pct, badgeType: d.badgeType };
      }
      return null;
    } catch (e) { return null; }
  }

  /** Badge label for a discount type. i18n strings are passed through opts (from the theme). */
  function discountBadge(badgeType, o) {
    o = o || {};
    if (badgeType === 'second_oil') return o.secondBottleBadge || '2nd bottle';
    if (badgeType === 'companion') return o.withDiffuserBadge || 'with diffuser';
    return o.saleBadge || '';
  }

  /**
   * Build one upsell slide. Markup mirrors the theme's native slides exactly, so our slides are
   * visually indistinguishable and reuse the same add/select handlers. Discount pricing is
   * data-driven from the live cartDiscounts (variantDiscount above) — never a hardcoded %.
   * opts: {recoKey, slot, money, addLabel, withDiffuserBadge, secondBottleBadge}
   */
  function buildSlide(p, o) {
    o = o || {};
    var money = o.money || function (c) { return '$' + (c / 100).toFixed(2); };
    var variants = p.variants || [];
    var v = null;
    for (var k = 0; k < variants.length; k++) { if (variants[k].available) { v = variants[k]; break; } }
    if (!v) v = variants[0] || { id: '', price: 0, title: '' };

    var img = p.featured_image;
    if (img && typeof img === 'object') img = img.src || '';
    img = img || '';
    // Request a 140px rendition from the Shopify CDN — featured_image URLs are the ORIGINAL
    // asset (often 100KB-2MB) and were being loaded into a 70px thumbnail.
    if (img && img.indexOf('cdn.shopify.com') !== -1 && img.indexOf('width=') === -1) {
      img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=140';
    }

    var priceHtml;
    var disc = variantDiscount(v.id, v.price);
    if (disc) {
      var discounted = Math.round(v.price * (1 - disc.pct));
      var badge = discountBadge(disc.badgeType, o);
      priceHtml = '<s>' + money(v.price) + '</s> <strong class="cart-drawer__companion-price">' +
        money(discounted) + '</strong>' + (badge ? ' <span class="cart-drawer__upsell-badge">' + badge + '</span>' : '');
    } else if (v.compare_at_price && v.compare_at_price > v.price) {
      priceHtml = '<s>' + money(v.compare_at_price) + '</s> <strong>' + money(v.price) + '</strong>';
    } else {
      priceHtml = '<strong>' + money(v.price) + '</strong>';
    }

    var selectHtml = '';
    if (variants.length > 1) {
      selectHtml = '<select class="cart-drawer__upsell-select" data-upsell-select>';
      for (var j = 0; j < variants.length; j++) {
        var vr = variants[j];
        if (vr.available) {
          selectHtml += '<option value="' + vr.id + '" data-price="' + vr.price + '" data-compare="' +
            (vr.compare_at_price || 0) + '">' + escapeHtml(vr.title) + '</option>';
        }
      }
      selectHtml += '</select>';
    }

    var safeTitle = escapeHtml(p.title);
    var recAttr = o.recoKey ? ' data-reco-key="' + escapeHtml(o.recoKey) + '" data-reco-slot="' + (o.slot || 0) + '"' : '';
    // Diffusers are a considered purchase — make the IMAGE a link to the PDP (new tab, so the cart
    // is never lost). Oils/collections stay pure one-click add. Kept in sync with native + Liquid.
    var isDiffuser = /diffuser/i.test(String(p.title)) && !/bulb/i.test(String(p.title));
    var productHref = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'products/' + encodeURIComponent(p.handle || '');
    var imgTag = '<img class="cart-drawer__upsell-img" src="' + img + '" alt="' + safeTitle + '" loading="lazy" width="70" height="70">';
    if (isDiffuser && p.handle) {
      imgTag = '<a class="cart-drawer__upsell-imglink" href="' + escapeHtml(productHref) + '" target="_blank" rel="noopener" aria-label="' + safeTitle + '">' + imgTag + '</a>';
    }
    var titleHtml = safeTitle;
    if (isDiffuser && p.handle) {
      titleHtml = '<a class="cart-drawer__upsell-titlelink" href="' + escapeHtml(productHref) + '" target="_blank" rel="noopener">' + safeTitle + '</a>';
    }
    // Button is a SIBLING of .upsell-info (not inside it) so it renders BESIDE the dropdown
    // (compact "+ Add", saves a row) — matching the server L1 layout. All 3 render paths must agree.
    return '<div class="cart-drawer__upsell-slide"' + recAttr + '>' +
        imgTag +
        '<div class="cart-drawer__upsell-info">' +
          '<div class="cart-drawer__upsell-title">' + titleHtml + '</div>' +
          (o.recCategory === 'bulb' && o.recReason
            ? '<div class="cart-drawer__upsell-reason">' + escapeHtml(o.recReason) + '</div>'
            : '') +
          '<div class="cart-drawer__upsell-price">' + priceHtml + '</div>' +
          selectHtml +
        '</div>' +
        '<button class="cart-drawer__upsell-add" data-variant-id="' + v.id + '" data-price="' + (v.price || 0) + '" data-variant-title="' + escapeHtml(v.title || '') + '">+ ' + (o.addLabel || 'Add') + '</button>' +
      '</div>';
  }

  /**
   * Main entry. Returns 'rendered' | 'fallback' | 'skip'.
   * opts: {
   *   config:{enabled,base,timeoutMs}, country, currency,
   *   getCart:()=>Promise<cart>, hydrate:(handle)=>Promise<product|null>,
   *   trackEl, containerEl, showCompanion, money, addLabel, withDiffuserBadge,
   *   sendBeacon:(url,bodyObj)=>void, onRendered:()=>void, onFallback:()=>void
   * }
   */
  function load(opts) {
    var cfg = opts.config || {};
    var fallback = function () { if (opts.onFallback) opts.onFallback(); return 'fallback'; };
    if (!cfg.enabled) return Promise.resolve(fallback());
    if (!opts.trackEl) return Promise.resolve('skip');

    return opts.getCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) return 'skip';
      var payload = {
        cartToken: cart.token,
        country: opts.country || '',
        items: cart.items.map(function (i) {
          // linePrice (minor units, cart currency): lets the backend tell a $0 GIFT oil from a bought one.
          return { title: i.product_title || i.title, quantity: i.quantity, properties: i.properties,
                   linePrice: (i.final_line_price != null ? i.final_line_price : i.line_price) };
        })
      };
      // QA override: pin the forced arm so the backend (normalizeAb honours a pinned abVariant) serves it.
      var _forced = forcedArm();
      if (_forced) payload.abVariant = _forced;
      return postJSON(cfg.base + '/cart/recommendations', payload, cfg.timeoutMs).then(function (data) {
        data = data || {};
        // Expose for the add-to-cart beacon (attribution on click).
        window.__cartRecoLast = { data: data, token: cart.token, opts: { country: opts.country, currency: opts.currency }, cartItems: (cart && cart.items) || [], cartDiscountCodes: ((cart && cart.discount_codes) || []).map(function (d) { return (d && d.code) || d; }), itemsSubtotalPrice: (cart && cart.items_subtotal_price) || 0, discountThreshold: opts.discountThreshold || 0, companionBuyProductIds: (opts.companionBuyProductIds || []).map(String) };

        // Symmetric exposure impression for BOTH arms (control included) — the KPI denominator.
        if (data.serve && opts.sendBeacon) {
          opts.sendBeacon(cfg.base + '/cart/reco-event', beaconBody('impression', cart.token, data, opts, [{ key: '', slot: 0 }]));
        }

        if (opts.impressionOnly) return 'impression';   // control: logged the impression, no DOM
        // ARM-AGNOSTIC: any served non-control arm renders our slots. (control took the impressionOnly
        // path above and never reaches here.) This was `=== 'A' || === 'B'`, which silently dropped
        // arm C to the native fallback for the whole 2026-07-19→23 run — family-first was never served.
        // Keep it arm-agnostic so new arms (C/D/…) actually render + fire their add beacons.
        var isTreatment = !!data.abVariant && data.abVariant !== 'control';
        if (!isTreatment || !data.recommendations || !data.recommendations.length) return fallback();

        // Hydrate every rec to a full product object (localized price/image/variants) IN PARALLEL.
        // Promise.all keeps array order, so slot order is unchanged; unavailable/failed ones drop out.
        // Why this matters (measured on production 2026-09-04, cold cache, 3 cards):
        //   serial  2702→3127, 3235→4039, 4045→4556  = 1854ms, each waiting for the previous
        //   parallel                                  ≈ the slowest single fetch (~800ms)
        // /products/{handle}.js costs ~0.7-0.9s each, so the chain was the whole delay behind the
        // "Matching your diffuser…" skeleton. Diagnosed 2026-08-01; that deploy only landed the JSON
        // cache in cart-drawer.liquid and this file kept the serial chain in production until now
        // (see cart_drawer_perf memory: "半个部署"). Warm cache was already fast, so it only ever
        // hurt first-time visitors.
        return Promise.all(data.recommendations.map(function (rec) {
          return opts.hydrate(rec.handle).then(function (p) {
            return (p && p.available) ? { product: p, rec: rec } : null;
          }).catch(function () { return null; });
        })).then(function (hydrated) {
          var products = hydrated.filter(function (x) { return !!x; });
          if (!products.length) return fallback();
          opts.trackEl.innerHTML = products.map(function (x) {
            return buildSlide(x.product, {
              showCompanion: opts.showCompanion, recoKey: x.rec.key, slot: x.rec.slot,
              money: opts.money, addLabel: opts.addLabel, withDiffuserBadge: opts.withDiffuserBadge,
              // EXP_BULB: the glass-bulb card's whole pitch is "fits your diffuser, MINI included",
              // and that is invisible in the photo. Passed for EVERY card but buildSlide renders it
              // for category 'bulb' ONLY — every other slide's DOM stays byte-identical, so the
              // control arm is untouched and this stays a one-variable experiment.
              recCategory: x.rec.category, recReason: x.rec.reason
            });
          }).join('');
          if (opts.containerEl) opts.containerEl.style.display = '';
          // Pre-compute each diffuser slide's bundled-scent properties NOW (we already have the cart
          // + bundleOils) and stash them on the element, so clicking Add fires /cart/add.js instantly
          // instead of doing a ~1-2s cart-fetch + title-hydrate at click time.
          precomputeDiffuserSlides(opts.trackEl, data.bundleOils, cart.items, opts.hydrate);
          if (opts.sendBeacon) {
            opts.sendBeacon(cfg.base + '/cart/reco-event', beaconBody('impression', cart.token, data, opts, data.recommendations));
          }
          if (opts.onRendered) opts.onRendered();
          return 'rendered';
        });
      });
    }).catch(function () { return fallback(); });
  }

  /** Fire an 'add' beacon when a rendered rec slide is added to cart. Safe no-op otherwise. */
  function trackAdd(slideEl, variantId) {
    try {
      var last = window.__cartRecoLast;
      var cfg = window.__cartRecoConfig || {};
      if (!slideEl || !slideEl.getAttribute('data-reco-key') || !last || !cfg.base) return;
      // Variant price in local-currency cents, for per-arm reco-revenue attribution. If the shopper
      // switched variants, the selected <option>'s data-price is authoritative; else the button's.
      var price = 0;
      var sel = slideEl.querySelector('[data-upsell-select]');
      if (sel && sel.options[sel.selectedIndex]) price = parseInt(sel.options[sel.selectedIndex].getAttribute('data-price') || '0', 10);
      if (!price) {
        var btn = slideEl.querySelector('.cart-drawer__upsell-add');
        if (btn) price = parseInt(btn.getAttribute('data-price') || '0', 10);
      }
      var body = beaconBody('add', last.token, last.data, last.opts, [{
        key: slideEl.getAttribute('data-reco-key'),
        variant_id: variantId,
        slot: parseInt(slideEl.getAttribute('data-reco-slot') || '0', 10)
      }]);
      if (price > 0) body.events[0].revenueCents = price;
      sendEvent(cfg.base + '/cart/reco-event', body);
      // Remember this reco-driven add so a later removal can be attributed as regret.
      rememberRecoAdd(variantId, {
        key: slideEl.getAttribute('data-reco-key'),
        slot: parseInt(slideEl.getAttribute('data-reco-slot') || '0', 10),
        cartToken: last.token, abVariant: last.data.abVariant, mode: last.data.mode,
        market: (last.opts || {}).country || '', currency: (last.opts || {}).currency || ''
      });
    } catch (e) { /* never break add-to-cart */ }
  }

  // ── Regret tracking: 'remove' events for reco-added items ──────────────────
  // Session-scoped map variantId -> add context. When the shopper later removes that
  // line (✕ button, or minus at qty 1), we emit a 'remove' event with the stored arm
  // context. Partial by design (covers drawer interactions); a pure telemetry add-on
  // that must never affect cart behaviour.
  var RECO_ADDS_KEY = '__cartRecoAdds';
  function readRecoAdds() {
    try { return JSON.parse(sessionStorage.getItem(RECO_ADDS_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function rememberRecoAdd(variantId, ctx) {
    try {
      var m = readRecoAdds();
      m[String(variantId)] = ctx;
      sessionStorage.setItem(RECO_ADDS_KEY, JSON.stringify(m));
    } catch (e) { /* storage unavailable — regret tracking silently off */ }
  }
  function maybeTrackRemove(variantId) {
    try {
      var cfg = window.__cartRecoConfig || {};
      if (!variantId || !cfg.base) return;
      var m = readRecoAdds();
      var ctx = m[String(variantId)];
      if (!ctx) return;
      delete m[String(variantId)];
      sessionStorage.setItem(RECO_ADDS_KEY, JSON.stringify(m));
      sendEvent(cfg.base + '/cart/reco-event', {
        cartToken: ctx.cartToken, abVariant: ctx.abVariant, mode: ctx.mode,
        market: ctx.market, currency: ctx.currency,
        events: [{ event: 'remove', key: ctx.key || '', variantId: String(variantId), slot: ctx.slot || 0 }]
      });
    } catch (e) { /* never break cart interactions */ }
  }
  // One delegated listener; variant id read from the row's qty input (data-quantity-variant-id).
  if (!window.__cartRecoRemoveHooked) {
    window.__cartRecoRemoveHooked = true;
    document.addEventListener('click', function (e) {
      try {
        var rm = e.target && e.target.closest && e.target.closest('cart-remove-button');
        var minus = !rm && e.target && e.target.closest && e.target.closest('button[name="minus"]');
        if (!rm && !minus) return;
        var row = (rm || minus).closest('.cart-item') || (rm || minus).closest('tr') || (rm || minus).closest('[id*="-Item-"]');
        if (!row) return;
        var qtyEl = row.querySelector('[data-quantity-variant-id]');
        if (!qtyEl) return;
        if (minus && String(qtyEl.value) !== '1') return;   // minus only counts when it empties the line
        maybeTrackRemove(qtyEl.getAttribute('data-quantity-variant-id'));
      } catch (err) { /* no-op */ }
    }, true);
  }

  // ── Diffuser bundled-scent auto-fill (quick-add has no PDP scent picker) ──────────
  // Slot count = the variant's Oil Size (each 30ml bottle = 1 slot). 0 if no oil size.
  //
  // 2026-09-16(老板「8/10/12精油在购物车添加 没有精油选项信息 要搞成默认的」):
  //   8/10/12 件套的变体名是 "Default Title",槽数不在变体名里、在**商品名**里
  //   ("The Grand Archive: 12-Piece Master Collection")—— 所以第二个参数传商品名,
  //   或 handle(形如 ...-30ml-x-12,不随语言变,比标题更稳)。
  //   与 PDP 的 essential-oil-selector 同一套约定:30/60/90/120ml → 1/2/3/4;8/10/12-Piece → 8/10/12。
  //   封顶 12(件套最大规格),ml 那条仍封顶 8。
  //   ⚠️ 依赖后端 getBundleOils 的 limit ≥ 12(已于 release 20260916_153008 提到 16),
  //      否则 12 件套只能填满 8 槽,剩下的还是空的。
  function parseOilSlots(variantTitle, productTitleOrHandle) {
    var m = String(variantTitle || '').match(/(\d+)\s*ml/i);
    if (m) return Math.max(0, Math.min(8, Math.round(parseInt(m[1], 10) / 30)));
    var s = String(productTitleOrHandle || '');
    var p = s.match(/(\d+)\s*-?\s*(?:piece|pc)\b/i) || s.match(/-x-(\d+)(?:-|$)/i);
    if (!p) return 0;
    return Math.max(0, Math.min(12, parseInt(p[1], 10)));
  }
  // 需要自动配默认精油的商品:机器(排除灯泡)或 N-Piece 精油件套。
  // ⚠️ 这里用商品名判定是**既有**做法,与本文件上方「买方件只认集合 id」是两件事:
  //    那边决定要不要显示折扣价(猜错=假价,红线);这里只决定要不要预填默认香型
  //    (猜错=多/少一份香型属性,不涉及价格)。别把两者混为一谈去「统一」。
  function isScentBundle(title) {
    var t = String(title || '');
    if (/bulb/i.test(t)) return false;
    return /diffuser/i.test(t) || /(\d+)\s*-?\s*(?:piece|pc)\b/i.test(t);
  }
  // Scent NO.<n> already in the cart — standalone oil lines AND scents bundled in cart diffusers.
  function cartOilNos(cartItems) {
    var nos = {};
    var scan = function(str) { var m = String(str || '').match(/NO\.?\s*(\d{1,3})/i); if (m) nos[parseInt(m[1], 10)] = true; };
    (cartItems || []).forEach(function(it) {
      scan(it.product_title || it.title);
      var props = it.properties;
      if (Array.isArray(props)) props.forEach(function(pp) { scan(pp && pp.value); });
      else if (props && typeof props === 'object') Object.keys(props).forEach(function(k) { scan(props[k]); });
    });
    return nos;
  }
  // Build the {Essential Oil – Option N: localizedTitle} properties for a quick-added diffuser:
  // pick N oils from the ranked bundle, dropping ones already in the cart; hydrate each handle to
  // its CURRENT-LOCALE product title (the value fulfillment expects). Returns null if nothing to add.
  function buildDiffuserScents(bundleOils, cartItems, slots, hydrate) {
    if (!bundleOils || !bundleOils.length || !slots || slots < 1) return Promise.resolve(null);
    var cartNos = cartOilNos(cartItems);
    var picked = [];
    bundleOils.forEach(function(o) { if (picked.length < slots && !cartNos[o.no]) picked.push(o); });
    // top up if exclusion left slots empty — a diffuser must ship its bottles (prefer any bundle oil)
    if (picked.length < slots) bundleOils.forEach(function(o) { if (picked.length < slots && picked.indexOf(o) < 0) picked.push(o); });
    return Promise.all(picked.map(function(o) {
      return hydrate(o.handle).then(function(pr) { return (pr && pr.title) ? pr.title : null; }).catch(function() { return null; });
    })).then(function(titles) {
      var props = {}, idx = 0;
      titles.forEach(function(t) { if (t) { idx++; props['Essential Oil \u2013 Option ' + idx] = t; } });
      return idx ? props : null;
    });
  }

  // Pre-fill slide.__scentProps for every diffuser slide in a track (undefined = not computed yet;
  // null = diffuser but no scents; object = ready). Called after render so the Add is instant.
  function precomputeDiffuserSlides(trackEl, bundleOils, cartItems, hydrate) {
    if (!trackEl || !bundleOils || !bundleOils.length) return;
    Array.prototype.slice.call(trackEl.querySelectorAll('.cart-drawer__upsell-slide')).forEach(function(slide) {
      var t = (slide.querySelector('.cart-drawer__upsell-title') || {}).textContent || '';
      // 2026-09-16:件套(N-Piece)也预算默认精油,不再只认 diffuser。
      if (!isScentBundle(t)) return;
      var btn = slide.querySelector('.cart-drawer__upsell-add');
      var sel = slide.querySelector('[data-upsell-select]');
      var vTitle = (sel && sel.options[sel.selectedIndex]) ? sel.options[sel.selectedIndex].text : (btn && btn.getAttribute('data-variant-title') || '');
      var slots = parseOilSlots(vTitle, t);
      buildDiffuserScents(bundleOils, cartItems, slots, hydrate).then(function(props) { slide.__scentProps = props || null; }).catch(function() {});
    });
  }

  window.CartRecoV2 = { load: load, buildSlide: buildSlide, trackAdd: trackAdd, sendEvent: sendEvent, abBucket: abBucket,
    parseOilSlots: parseOilSlots, isScentBundle: isScentBundle, cartOilNos: cartOilNos, buildDiffuserScents: buildDiffuserScents,
    precomputeDiffuserSlides: precomputeDiffuserSlides, variantDiscount: variantDiscount, discountBadge: discountBadge,
    _postJSON: postJSON };
})();
